import { useMemo, useState, type FormEvent } from 'react'
import {
  CAlert,
  CButton,
  CForm,
  CFormInput,
  CFormSelect,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from '@coreui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { auditCategories, machineStates } from '@larice/contracts'
import { consoleApi, apiErrorMessage } from '../../api/client'
import type { AuditDraft } from '../../api/types'
import { useAuth } from '../../auth/AuthProvider'
import { EmptyState, ErrorState, LoadingState } from '../../components/common/AsyncState'
import { PageHeader } from '../../components/common/PageHeader'
import { formatDateTime } from '../../utils/format'

const localDateTime = (date = new Date()) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
const initial = {
  category: 'workload',
  title: '',
  summary: '',
  details: '',
  occurredAt: localDateTime(),
  visibility: 'public',
  embargoUntil: '',
  source: 'manual',
  stateBefore: '',
  stateAfter: '',
  projectAlias: '',
  correctionOf: '',
}

export function DraftsPage() {
  const auth = useAuth()
  const client = useQueryClient()
  const [status, setStatus] = useState('')
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(initial)
  const [actionError, setActionError] = useState('')
  const query = useQuery({
    queryKey: ['console', 'drafts', status],
    queryFn: () => consoleApi.drafts(status || undefined),
  })
  const refresh = async () => {
    await client.invalidateQueries({ queryKey: ['console', 'drafts'] })
    await client.invalidateQueries({ queryKey: ['console', 'dashboard'] })
  }
  const create = useMutation({
    mutationFn: consoleApi.createDraft,
    onSuccess: async () => {
      setModal(false)
      setForm(initial)
      await refresh()
    },
  })
  const submitDraft = useMutation({ mutationFn: consoleApi.submitDraft, onSuccess: refresh })
  const approve = useMutation({ mutationFn: consoleApi.approveDraft, onSuccess: refresh })
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => consoleApi.rejectDraft(id, reason),
    onSuccess: refresh,
  })
  const busy = create.isPending || submitDraft.isPending || approve.isPending || reject.isPending
  const error = create.error ?? submitDraft.error ?? approve.error ?? reject.error

  const items = useMemo(() => query.data ?? [], [query.data])
  const submit = (event: FormEvent) => {
    event.preventDefault()
    setActionError('')
    try {
      create.mutate({
        category: form.category,
        title: form.title,
        summary: form.summary,
        ...(form.details ? { details: form.details } : {}),
        occurredAt: new Date(form.occurredAt).toISOString(),
        visibility: form.visibility,
        embargoUntil:
          form.visibility === 'embargoed' && form.embargoUntil
            ? new Date(form.embargoUntil).toISOString()
            : null,
        source: form.source,
        stateBefore: form.stateBefore || null,
        stateAfter: form.stateAfter || null,
        ...(form.projectAlias ? { projectAlias: form.projectAlias } : {}),
        evidence: [],
        correctionOf: form.category === 'correction' ? form.correctionOf : null,
      })
    } catch (caught) {
      setActionError(apiErrorMessage(caught))
    }
  }
  const requestReject = (item: AuditDraft) => {
    const reason = window
      .prompt('Explain why this public draft needs revision (at least 10 characters):')
      ?.trim()
    if (reason && reason.length >= 10) reject.mutate({ id: item._id, reason })
  }

  return (
    <>
      <PageHeader
        eyebrow="Append-only publication workflow"
        title="Audit drafts"
        description="Create a public-safe record, submit it for independent review, and publish it to the hash-chained Compute Ledger."
        actions={
          <div className="page-actions">
            <CFormSelect
              aria-label="Filter draft status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="rejected">Rejected</option>
              <option value="published">Published</option>
            </CFormSelect>
            <CButton color="primary" onClick={() => setModal(true)}>
              New audit draft
            </CButton>
          </div>
        }
      />
      {error && <CAlert color="danger">{apiErrorMessage(error)}</CAlert>}
      {query.isLoading && <LoadingState />}
      {query.isError && <ErrorState message={apiErrorMessage(query.error)} />}
      {!query.isLoading && items.length === 0 && (
        <EmptyState
          title="No drafts in this view"
          description="Create a draft or change the status filter."
        />
      )}
      {items.length > 0 && (
        <div className="draft-list">
          {items.map((item) => (
            <article key={item._id} className="draft-card">
              <div className="draft-card__header">
                <div>
                  <span className={`draft-status draft-${item.status}`}>{item.status}</span>
                  <span className="category">{item.category}</span>
                </div>
                <code>{item._id.slice(-8)}</code>
              </div>
              <h2>{item.title}</h2>
              <p>{item.summary}</p>
              <dl>
                <div>
                  <dt>Occurred</dt>
                  <dd>{formatDateTime(item.occurredAt)}</dd>
                </div>
                <div>
                  <dt>Visibility</dt>
                  <dd>{item.visibility}</dd>
                </div>
                <div>
                  <dt>Source</dt>
                  <dd>{item.source}</dd>
                </div>
                <div>
                  <dt>Author</dt>
                  <dd>{item.createdBy}</dd>
                </div>
              </dl>
              {item.reviewReason && (
                <CAlert color="warning">
                  <strong>Review note:</strong> {item.reviewReason}
                </CAlert>
              )}
              <div className="draft-card__actions">
                {['draft', 'rejected'].includes(item.status) && item.createdBy === auth.user?.uid && (
                  <CButton
                    color="primary"
                    variant="outline"
                    disabled={busy}
                    onClick={() => submitDraft.mutate(item._id)}
                  >
                    Submit for review
                  </CButton>
                )}
                {item.status === 'submitted' &&
                  auth.hasRole('reviewer') &&
                  item.createdBy !== auth.user?.uid && (
                    <>
                      <CButton color="success" disabled={busy} onClick={() => approve.mutate(item._id)}>
                        Approve & publish
                      </CButton>
                      <CButton
                        color="danger"
                        variant="outline"
                        disabled={busy}
                        onClick={() => requestReject(item)}
                      >
                        Return for revision
                      </CButton>
                    </>
                  )}
                {item.status === 'published' && item.publishedEventId && (
                  <a className="btn btn-light" href={`/ledger/${item.publishedEventId}`}>
                    Open public event
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
      <CModal visible={modal} onClose={() => setModal(false)} size="lg" backdrop="static">
        <CModalHeader>
          <CModalTitle>New public audit draft</CModalTitle>
        </CModalHeader>
        <CForm onSubmit={submit}>
          <CModalBody>
            {(actionError || create.error) && (
              <CAlert color="danger">{actionError || apiErrorMessage(create.error)}</CAlert>
            )}
            <div className="form-grid">
              <label>
                Category
                <CFormSelect
                  value={form.category}
                  onChange={(event) => setForm({ ...form, category: event.target.value })}
                >
                  {auditCategories.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </CFormSelect>
              </label>
              <label>
                Occurred at
                <CFormInput
                  type="datetime-local"
                  value={form.occurredAt}
                  onChange={(event) => setForm({ ...form, occurredAt: event.target.value })}
                  required
                />
              </label>
              <label className="form-grid__full">
                Public title
                <CFormInput
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                  minLength={3}
                  maxLength={180}
                  required
                />
              </label>
              <label className="form-grid__full">
                Public summary
                <CFormTextarea
                  rows={3}
                  value={form.summary}
                  onChange={(event) => setForm({ ...form, summary: event.target.value })}
                  minLength={10}
                  maxLength={600}
                  required
                />
              </label>
              <label className="form-grid__full">
                Additional public details (optional)
                <CFormTextarea
                  rows={4}
                  value={form.details}
                  onChange={(event) => setForm({ ...form, details: event.target.value })}
                  maxLength={5000}
                />
              </label>
              <label>
                Visibility
                <CFormSelect
                  value={form.visibility}
                  onChange={(event) => setForm({ ...form, visibility: event.target.value })}
                >
                  <option value="public">Public after approval</option>
                  <option value="embargoed">Public after embargo</option>
                  <option value="internal">Internal only</option>
                </CFormSelect>
              </label>
              {form.visibility === 'embargoed' && (
                <label>
                  Embargo until
                  <CFormInput
                    type="datetime-local"
                    value={form.embargoUntil}
                    onChange={(event) => setForm({ ...form, embargoUntil: event.target.value })}
                    required
                  />
                </label>
              )}
              <label>
                Evidence source
                <CFormSelect
                  value={form.source}
                  onChange={(event) => setForm({ ...form, source: event.target.value })}
                >
                  <option value="manual">Member submitted</option>
                  <option value="imported">Imported evidence</option>
                  {auth.hasRole('operator') && <option value="automatic">Automatically observed</option>}
                </CFormSelect>
              </label>
              <label>
                Project alias
                <CFormInput
                  value={form.projectAlias}
                  onChange={(event) => setForm({ ...form, projectAlias: event.target.value })}
                  placeholder="LR-024"
                />
              </label>
              <label>
                State before
                <CFormSelect
                  value={form.stateBefore}
                  onChange={(event) => setForm({ ...form, stateBefore: event.target.value })}
                >
                  <option value="">Not applicable</option>
                  {machineStates.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </CFormSelect>
              </label>
              <label>
                State after
                <CFormSelect
                  value={form.stateAfter}
                  onChange={(event) => setForm({ ...form, stateAfter: event.target.value })}
                >
                  <option value="">Not applicable</option>
                  {machineStates.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </CFormSelect>
              </label>
              {form.category === 'correction' && (
                <label className="form-grid__full">
                  Event ID being corrected
                  <CFormInput
                    value={form.correctionOf}
                    onChange={(event) => setForm({ ...form, correctionOf: event.target.value })}
                    placeholder="EVT-YYYYMMDD-000001"
                    required
                  />
                </label>
              )}
            </div>
            <div className="form-note">
              <strong>Public-data check</strong>
              <p>
                Do not include usernames, commands, hostnames, private endpoints, raw log content,
                internal IP addresses or confidential research details.
              </p>
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton color="light" onClick={() => setModal(false)}>
              Cancel
            </CButton>
            <CButton color="primary" type="submit" disabled={create.isPending}>
              {create.isPending && <CSpinner size="sm" />} Save draft
            </CButton>
          </CModalFooter>
        </CForm>
      </CModal>
    </>
  )
}
